import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import type {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';

import { auditApi } from '../api';
import { pickSetFilterFirstValue, pickTextFilter } from '../util/datasource';

export type FeedFilters = {
  objectType: string;
  q: string;
  /** ISO timestamp (inclusive). Empty string means open-ended. */
  startDate: string;
};

/**
 * ag-grid server-side datasource for `/api/v1/audit/feed`. Paging, the
 * operation/user column filters and the toolbar's entity, search and date
 * filters are all pushed down to the audit service.
 */
export function useAuditFeedDatasource(filters: FeedFilters) {
  const filtersRef = useRef(filters);
  useLayoutEffect(() => {
    filtersRef.current = filters;
  });

  const getRows = useCallback(async (params: IServerSideGetRowsParams) => {
    const { request, api } = params;
    const startRow = request.startRow ?? 0;
    const endRow = request.endRow ?? startRow + 50;
    const filterModel = (request.filterModel ?? {}) as Record<
      string,
      FilterModel
    >;
    const current = filtersRef.current;

    try {
      const response = await auditApi.fetchAuditFeed({
        objectType: current.objectType || undefined,
        skip: startRow,
        take: Math.max(1, endRow - startRow),
        q: current.q || undefined,
        startDate: current.startDate || undefined,
        operationType:
          pickSetFilterFirstValue(filterModel.operationType) || undefined,
        userEmail: pickTextFilter(filterModel.userEmail) || undefined,
      });

      const rowData = response.items;
      params.success({
        rowData,
        rowCount: response.hasMore ? undefined : startRow + rowData.length,
      });

      if (startRow === 0 && rowData.length === 0) api?.showNoRowsOverlay();
      else if (rowData.length > 0) api?.hideOverlay();
    } catch (err) {
      console.error('Audit feed fetch failed', err);
      params.fail();
    }
  }, []);

  return useMemo<IServerSideDatasource>(() => ({ getRows }), [getRows]);
}
